import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import SEOHead, { buildBreadcrumbs } from '../components/SEOHead'
import { NalaIcon } from '../components/nala/NalaIcon'
import { submitLead } from '../services/leads'
import { trackBookVisitConversion } from '../utils/analytics'
import { loadGoogleMaps } from '../lib/loadGoogleMaps'

const SITE_URL = 'https://nala.vet'

// ── Service catalogue ── mirrors BookingModal exactly so pricing is consistent
const SERVICES = [
  { name: 'Wellness exam',           price: '$150'           },
  { name: 'Urgent visit',            price: '$200'           },
  { name: 'Senior & chronic care',   price: '$300/month'     },
  { name: 'Microchipping',           price: '$75'            },
  { name: 'End-of-life care',        price: '$450'           },
  { name: 'International certificate', price: '$350 + USDA fees' },
] as const

const TIME_WINDOWS = [
  { key: 'morning',   label: 'Morning',   hours: '8:00 AM – 1:00 PM' },
  { key: 'afternoon', label: 'Afternoon', hours: '1:00 PM – 5:00 PM' },
  { key: 'evening',   label: 'Evening',   hours: '5:00 PM – 9:00 PM' },
] as const

const STEPS = [
  { title: 'Tell us about your pet',   sub: 'A few details so the right vet shows up.'       },
  { title: 'Where should we visit?',   sub: 'We serve homes across Miami-Dade.'  },
  { title: 'Pick a time',              sub: 'All visits include a full home consultation.'    },
  { title: 'Almost done',              sub: "We'll text a confirmation and a 30-min heads-up." },
] as const

interface BookingData {
  petName: string
  petKind: 'Dog' | 'Cat'
  service: string
  address: string
  address_street: string
  address_city: string
  address_state: string
  address_zip: string
  notes: string
  day: number
  slot: string
  name: string
  phone: string
  email: string
}

const INIT: BookingData = {
  petName: '', petKind: 'Dog', service: 'Wellness exam',
  address: '', address_street: '', address_city: '', address_state: '', address_zip: '',
  notes: '',
  day: 0, slot: '',
  name: '', phone: '', email: '',
}

export default function BookingPage() {
  const [step, setStep] = useState(0)
  const [data, setData] = useState<BookingData>(INIT)
  const addressInputRef = useRef<HTMLInputElement>(null)

  const set = <K extends keyof BookingData>(k: K, v: BookingData[K]) =>
    setData((d) => ({ ...d, [k]: v }))

  const days = useMemo(() => {
    const today = new Date()
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(today)
      d.setDate(today.getDate() + i)
      return d
    })
  }, [])

  // Scroll to top on every step change
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }) }, [step])

  // Attach Google Places Autocomplete to the address <input> as a progressive
  // enhancement. The input itself is the source of truth for data.address, so
  // the step is always completable by typing manually — even if Maps never
  // loads (no API key, blocked, offline). loadGoogleMaps() resolves false when
  // there's no usable key, in which case we simply skip attaching.
  useEffect(() => {
    if (step !== 1) return
    let cancelled = false
    let cleanup: (() => void) | undefined

    loadGoogleMaps().then((ok) => {
      if (cancelled || !ok || !addressInputRef.current) return
      const places = (window as any).google?.maps?.places
      if (!places?.Autocomplete) return

      const styleId = 'gmp-autocomplete-styles'
      if (!document.getElementById(styleId)) {
        const style = document.createElement('style')
        style.id = styleId
        style.textContent = `.pac-container { z-index: 99999 !important; }`
        document.head.appendChild(style)
      }

      const autocomplete = new places.Autocomplete(addressInputRef.current, {
        componentRestrictions: { country: 'us' },
        types: ['address'],
        fields: ['address_components', 'formatted_address'],
      })

      const listener = autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace()
        const comps = place.address_components ?? []
        const get = (type: string) =>
          comps.find((c: any) => c.types.includes(type))?.long_name ?? ''
        const getShort = (type: string) =>
          comps.find((c: any) => c.types.includes(type))?.short_name ?? ''

        const streetNumber = get('street_number')
        const route = get('route')
        const city = get('locality') || get('sublocality')
        const state = getShort('administrative_area_level_1')
        const zip = get('postal_code')

        const fullAddress = place.formatted_address
          || `${streetNumber} ${route}, ${city}, ${state} ${zip}`.trim()

        setData((d) => ({
          ...d,
          address: fullAddress,
          address_street: `${streetNumber} ${route}`.trim(),
          address_city: city,
          address_state: state,
          address_zip: zip,
        }))
      })

      cleanup = () => {
        listener?.remove?.()
        ;(window as any).google?.maps?.event?.clearInstanceListeners?.(autocomplete)
      }
    })

    return () => {
      cancelled = true
      cleanup?.()
    }
  }, [step])

  const stepValid = [
    !!data.petName.trim() && !!data.service,
    !!data.address.trim(),
    !!data.slot,
    !!data.name.trim() && !!data.phone.trim() && data.email.includes('@'),
  ][step]

  const buildNotes = () => {
    const day = days[data.day]
    const windowLabel = TIME_WINDOWS.find((w) => w.key === data.slot)?.label ?? data.slot
    return [
      `Service: ${data.service}`,
      `Preferred: ${day.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })} · ${windowLabel}`,
      `Address: ${data.address}`,
      data.notes ? `Notes: ${data.notes}` : null,
    ]
      .filter(Boolean)
      .join('\n')
  }

  const mutation = useMutation({
    mutationFn: () => {
      const [first_name, ...rest] = data.name.trim().split(/\s+/)
      return submitLead({
        first_name: first_name || data.name,
        last_name:  rest.join(' ') || undefined,
        email:      data.email  || undefined,
        phone:      data.phone  || undefined,
        pet_name:   data.petName || undefined,
        pet_species: data.petKind.toLowerCase(),
        notes:      buildNotes(), // includes appointment window preference
        source:     'booking_page',
        market_slug: 'miami',
        address_street: data.address_street || undefined,
        address_city:   data.address_city || undefined,
        address_state:  data.address_state || undefined,
        address_zip:    data.address_zip || undefined,
      })
    },
    onSuccess: () => {
      // Push the "Book a visit" conversion into the GTM dataLayer so the
      // container can fire the Google Ads / GA4 conversion tag.
      trackBookVisitConversion({
        source: 'booking_page',
        service: data.service,
        petSpecies: data.petKind.toLowerCase(),
        preferredWindow: data.slot,
      })
    },
  })

  const next = () => {
    if (step < 3) setStep((s) => s + 1)
    else mutation.mutate()
  }
  const back = () => setStep((s) => Math.max(0, s - 1))

  const submitted = mutation.isSuccess

  const selectedService = SERVICES.find((s) => s.name === data.service)

  return (
    <>
      <SEOHead
        title="Book a Home Vet Visit — Nala"
        description="Book an in-home veterinary visit in minutes. Wellness exams, vaccinations, sick visits, and more — a licensed vet comes to you in Miami-Dade."
        canonical={`${SITE_URL}/book`}
        jsonLd={[buildBreadcrumbs([{ name: 'Book a Visit', url: `${SITE_URL}/book` }])]}
      />

      <div className="min-h-screen bg-nala-surface">
        <div className="max-w-2xl mx-auto px-5 py-12 md:py-20">

          {submitted ? (
            /* ── Confirmation ─────────────────────────────────────────── */
            <div className="bg-white border border-nala-line rounded-3xl p-10 text-center shadow-sm">
              <div className="w-16 h-16 rounded-2xl bg-nala-soft flex items-center justify-center mx-auto mb-6">
                <NalaIcon.Check size={28} color="#1f3a5f" />
              </div>
              <h1 className="font-fraunces text-4xl font-[400] text-nala-ink tracking-tight">
                You&apos;re on the list.
              </h1>
              <p className="text-nala-mute mt-3 text-[15px] leading-relaxed max-w-sm mx-auto">
                We&apos;ll text{data.phone ? ` ${data.phone}` : ' you'} a confirmation, and again
                30 minutes before{' '}
                {days[data.day].toLocaleDateString('en-US', { weekday: 'long' })}&apos;s visit.
              </p>
              <div className="mt-6 inline-block px-5 py-3 bg-nala-soft rounded-xl text-sm text-nala-ink font-medium">
                {days[data.day].toLocaleDateString('en-US', {
                  weekday: 'long', month: 'short', day: 'numeric',
                })}{' '}
                · {TIME_WINDOWS.find((w) => w.key === data.slot)?.label ?? data.slot} · {data.service}
              </div>
              <div className="mt-8">
                <button
                  onClick={() => { setStep(0); setData(INIT); mutation.reset() }}
                  className="text-sm text-nala-mute hover:text-nala-ink transition"
                >
                  Book another visit
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* ── Progress bar ──────────────────────────────────────── */}
              <div className="flex gap-2 mb-8">
                {STEPS.map((_, i) => (
                  <div
                    key={i}
                    className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                      i <= step ? 'bg-nala-accent' : 'bg-nala-line'
                    }`}
                  />
                ))}
              </div>

              {/* ── Step heading ──────────────────────────────────────── */}
              <div className="mb-8">
                <p className="text-xs font-semibold uppercase tracking-widest text-nala-mute mb-2">
                  Step {step + 1} of 4
                </p>
                <h1 className="font-fraunces text-4xl md:text-5xl font-[400] text-nala-ink tracking-tight leading-tight">
                  {STEPS[step].title}
                </h1>
                <p className="text-nala-mute mt-2 text-[15px]">{STEPS[step].sub}</p>
              </div>

              {/* ── Step body ─────────────────────────────────────────── */}
              <div className="bg-white border border-nala-line rounded-3xl p-7 shadow-sm space-y-6">

                {step === 0 && (
                  <>
                    <BookField label="Pet's name">
                      <BookInput
                        value={data.petName}
                        onChange={(v) => set('petName', v)}
                        placeholder="e.g. Luna"
                        autoFocus
                      />
                    </BookField>

                    <BookField label="Species">
                      <div className="inline-flex p-1 bg-nala-soft rounded-xl gap-1">
                        {(['Dog', 'Cat'] as const).map((o) => (
                          <button
                            key={o}
                            type="button"
                            onClick={() => set('petKind', o)}
                            className={`px-6 py-2 rounded-lg text-sm font-medium transition ${
                              data.petKind === o
                                ? 'bg-white text-nala-ink shadow-sm'
                                : 'text-nala-mute hover:text-nala-ink'
                            }`}
                          >
                            {o}
                          </button>
                        ))}
                      </div>
                    </BookField>

                    <BookField label="Reason for visit">
                      <div className="grid grid-cols-2 gap-3">
                        {SERVICES.map((s) => {
                          const sel = data.service === s.name
                          return (
                            <button
                              key={s.name}
                              type="button"
                              onClick={() => set('service', s.name)}
                              className={`text-left p-4 rounded-2xl border transition-all ${
                                sel
                                  ? 'border-nala-accent bg-nala-soft shadow-sm'
                                  : 'border-nala-line bg-white hover:border-nala-mute/40'
                              }`}
                            >
                              <div className="font-semibold text-nala-ink text-sm">{s.name}</div>
                              <div className="text-nala-mute text-xs mt-0.5">{s.price}</div>
                            </button>
                          )
                        })}
                      </div>
                    </BookField>
                  </>
                )}

                {step === 1 && (
                  <>
                    <BookField label="Your address">
                      <input
                        ref={addressInputRef}
                        type="text"
                        value={data.address}
                        onChange={(e) =>
                          setData((d) => ({
                            ...d,
                            address: e.target.value,
                            address_street: '',
                            address_city: '',
                            address_state: '',
                            address_zip: '',
                          }))
                        }
                        placeholder="Start typing your street address…"
                        autoComplete="off"
                        autoFocus
                        className="w-full rounded-xl border border-nala-line px-4 py-3 text-sm text-nala-ink focus:border-nala-accent focus:outline-none focus:ring-1 focus:ring-nala-accent placeholder:text-nala-line/80 transition bg-white"
                      />
                      {data.address_city && data.address_state && (
                        <div className="mt-1.5 text-xs text-nala-mute">
                          ✓ {data.address_street}, {data.address_city}, {data.address_state} {data.address_zip}
                        </div>
                      )}
                    </BookField>

                    <BookField label="Notes for the vet (optional)">
                      <textarea
                        value={data.notes}
                        onChange={(e) => set('notes', e.target.value)}
                        placeholder="Parking, building access, pet's temperament…"
                        rows={3}
                        className="w-full rounded-xl border border-nala-line px-4 py-3 text-sm text-nala-ink focus:border-nala-accent focus:outline-none focus:ring-1 focus:ring-nala-accent resize-none placeholder:text-nala-line/80"
                      />
                    </BookField>
                  </>
                )}

                {step === 2 && (
                  <>
                    {/* Day strip */}
                    <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
                      {days.map((d, i) => {
                        const sel = data.day === i
                        return (
                          <button
                            key={i}
                            type="button"
                            onClick={() => { set('day', i); set('slot', '') }}
                            className={`shrink-0 min-w-[68px] px-3 py-3 rounded-2xl border text-center transition-all ${
                              sel
                                ? 'border-nala-accent bg-nala-accent text-nala-accentInk'
                                : 'border-nala-line bg-white text-nala-ink hover:border-nala-mute/40'
                            }`}
                          >
                            <div className="text-[10px] uppercase tracking-widest opacity-80">
                              {i === 0 ? 'Today' : d.toLocaleDateString('en-US', { weekday: 'short' })}
                            </div>
                            <div className="font-fraunces text-xl font-[600] mt-0.5">
                              {d.getDate()}
                            </div>
                          </button>
                        )
                      })}
                    </div>

                    {/* Time windows */}
                    <div className="grid grid-cols-3 gap-3">
                      {TIME_WINDOWS.map((w) => {
                        const sel = data.slot === w.key
                        return (
                          <button
                            key={w.key}
                            type="button"
                            onClick={() => set('slot', w.key)}
                            className={`rounded-xl border p-4 text-left transition-colors ${
                              sel
                                ? 'border-nala-accent bg-nala-soft'
                                : 'border-nala-line bg-white hover:border-nala-accent/50'
                            }`}
                          >
                            <div className="font-semibold text-sm text-nala-ink">{w.label}</div>
                            <div className="text-xs text-nala-mute mt-0.5">{w.hours}</div>
                          </button>
                        )
                      })}
                    </div>

                    <p className="text-xs text-nala-mute">
                      The vet will confirm an exact arrival time within your selected window.
                    </p>
                  </>
                )}

                {step === 3 && (
                  <>
                    <BookField label="Your name">
                      <BookInput
                        value={data.name}
                        onChange={(v) => set('name', v)}
                        placeholder="Mia Chen"
                        autoFocus
                      />
                    </BookField>

                    <div className="grid grid-cols-2 gap-4">
                      <BookField label="Phone">
                        <BookInput
                          value={data.phone}
                          onChange={(v) => set('phone', v)}
                          placeholder="(305) 555-0184"
                          type="tel"
                        />
                      </BookField>
                      <BookField label="Email">
                        <BookInput
                          value={data.email}
                          onChange={(v) => set('email', v)}
                          placeholder="mia@example.com"
                          type="email"
                        />
                      </BookField>
                    </div>

                    {/* Visit summary */}
                    <div className="bg-nala-soft rounded-2xl p-5 space-y-2 text-sm">
                      <div className="font-semibold text-nala-ink mb-3">Visit summary</div>
                      <SummaryRow label="Pet"     value={`${data.petName || '—'} (${data.petKind})`} />
                      <SummaryRow label="Service" value={data.service} />
                      {selectedService && (
                        <SummaryRow label="Price" value={selectedService.price} />
                      )}
                      <SummaryRow label="Address" value={data.address || '—'} />
                      <SummaryRow
                        label="When"
                        value={`${days[data.day].toLocaleDateString('en-US', {
                          weekday: 'long', month: 'short', day: 'numeric',
                        })} · ${TIME_WINDOWS.find((w) => w.key === data.slot)?.label || data.slot || '—'}`}
                      />
                    </div>

                    {mutation.isError && (
                      <div className="px-4 py-3 rounded-xl bg-red-50 text-red-700 text-sm">
                        We couldn&apos;t submit just now. Please try again, or{' '}
                        <a href="tel:2138338007" className="underline">call us</a> directly.
                      </div>
                    )}

                    <p className="text-[11px] text-nala-mute leading-relaxed">
                      By tapping &ldquo;Confirm booking,&rdquo; you agree to receive appointment
                      and account text messages from Nala at the number provided (including
                      confirmations and reminders). Consent is not a condition of service. Msg
                      frequency varies; msg &amp; data rates may apply. Reply STOP to opt out,
                      HELP for help. See our{' '}
                      <a href="/policy" className="underline">Privacy Policy</a> and{' '}
                      <a href="/terms" className="underline">Terms</a>.
                    </p>
                  </>
                )}
              </div>

              {/* ── Navigation ────────────────────────────────────────── */}
              <div className="mt-6 flex items-center justify-between">
                <button
                  type="button"
                  onClick={back}
                  className="text-sm text-nala-mute hover:text-nala-ink transition px-2 py-1"
                >
                  {step === 0 ? '' : '← Back'}
                </button>

                <button
                  type="button"
                  onClick={next}
                  disabled={!stepValid || mutation.isPending}
                  className="bg-nala-accent hover:bg-nala-accent/90 text-nala-accentInk font-semibold rounded-full px-8 py-3 text-sm transition disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {step === 3
                    ? mutation.isPending ? 'Booking…' : 'Confirm booking'
                    : 'Continue →'}
                </button>
              </div>

              {/* ── Trust line ────────────────────────────────────────── */}
              {step === 0 && (
                <p className="mt-8 text-center text-xs text-nala-mute">
                  Licensed veterinarians · Miami-Dade · Same-week availability
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </>
  )
}

function BookField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-nala-mute uppercase tracking-wide mb-2">
        {label}
      </span>
      {children}
    </label>
  )
}

function BookInput({
  value, onChange, placeholder, autoFocus, type = 'text',
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  autoFocus?: boolean
  type?: string
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      autoFocus={autoFocus}
      className="w-full rounded-xl border border-nala-line px-4 py-3 text-sm text-nala-ink focus:border-nala-accent focus:outline-none focus:ring-1 focus:ring-nala-accent placeholder:text-nala-line/80 transition bg-white"
    />
  )
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 py-1 border-b border-nala-line/50 last:border-0">
      <span className="text-nala-mute">{label}</span>
      <span className="text-nala-ink text-right">{value}</span>
    </div>
  )
}
